import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { BarChart3, Activity, AlertCircle, CheckCircle, Calendar, CheckCircle as CheckCircleGreen } from "lucide-react";
import "./CycleInsightsTab.css";

/* ── Inline Icons ── */
const BarChartIcon = () => <BarChart3 size={18} />;
const ActivityIcon = () => <Activity size={18} />;
const AlertCircleIcon = () => <AlertCircle size={18} />;
const CheckCircleIcon = () => <CheckCircle size={18} />;
const CalendarIcon = () => <Calendar size={36} color="#9ca3af" />;
const OkCircleIcon = () => <CheckCircleGreen size={40} color="#22c55e" />;

/* ── Helpers ── */
function fmtDate(d) {
  if (!d) return "--";
  try {
    return new Date(d).toLocaleDateString("en-US", {
      month: "long", day: "numeric", year: "numeric",
    });
  } catch { return d; }
}

/* ── Sub-components ── */
function StatCard({ icon, label, value, sub, subColor, iconBg, iconColor }) {
  return (
    <div className="cit__stat-card">
      <div className="cit__stat-top">
        <span className="cit__stat-icon" style={{ background: iconBg, color: iconColor }}>
          {icon}
        </span>
        <span className="cit__stat-label">{label}</span>
      </div>
      <div className="cit__stat-value">{value ?? "--"}</div>
      {sub && (
        <div className="cit__stat-sub" style={subColor ? { color: subColor } : {}}>
          {sub}
        </div>
      )}
    </div>
  );
}

function CycleHistoryCard({ c, t }) {
  const isRegular = c.status === "normal" || !c.status;
  const cycleLen  = c.cycle_length  ? String(c.cycle_length).replace("j", "")  : null;
  const bleedLen  = c.bleeding_days ? String(c.bleeding_days).replace("j", "") : null;

  return (
    <div className="cit__history-card">
      <div className="cit__history-main">
        <div className="cit__history-dates">
          {fmtDate(c.start_date)} – {fmtDate(c.end_date)}
        </div>
        <div className="cit__history-meta">
          {cycleLen && `${t("cycleBot.insightsTab.cycleLength")}: ${cycleLen} ${t("cycleBot.insightsTab.days")}`}
          {cycleLen && bleedLen && " • "}
          {bleedLen && `${t("cycleBot.insightsTab.period")}: ${bleedLen} ${t("cycleBot.insightsTab.days")}`}
        </div>
        <div className="cit__history-tags">
          <span className="cit__history-tag">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#db2777" strokeWidth="2" strokeLinecap="round">
              <path d="M12 22c3.5 0 7-3.5 7-8S12 2 12 2 5 8.5 5 14s3.5 8 7 8z" />
            </svg>
            {t("cycleBot.insightsTab.moderateFlow")}
          </span>
          <span className="cit__history-tag">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#7c3aed" strokeWidth="2" strokeLinecap="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12" />
            </svg>
            {t("cycleBot.insightsTab.noSymptomsLogged")}
          </span>
        </div>
      </div>
      <span className={`cit__history-badge ${isRegular ? "cit__history-badge--regular" : "cit__history-badge--irregular"}`}>
        {isRegular ? t("cycleBot.insightsTab.regular") : t("cycleBot.insightsTab.irregular")}
      </span>
    </div>
  );
}

/* ── Main Component ── */
export default function CycleInsightsTab({ stats, cycles = [], alerts = [] }) {
  const { t } = useTranslation("cycle");
  const avgDuration      = stats?.avg_cycle_length != null ? `${stats.avg_cycle_length}d` : "--";
  const regularity       = stats?.regularity_score != null ? `${Math.round(stats.regularity_score)}%` : "--";
  const regularityLabel  = stats?.regularity_score >= 80
    ? t("cycleBot.insightsTab.excellent") : stats?.regularity_score >= 60 ? t("cycleBot.insightsTab.good") : stats?.regularity_score != null ? t("cycleBot.insightsTab.fair") : null;

  const STATS = [
    {
      icon:       <BarChartIcon />,
      label:      t("cycleBot.insightsTab.totalCycles"),
      value:      stats?.cycles_count ?? "--",
      sub:        t("cycleBot.insightsTab.last3Months"),
      iconBg:     "#dbeafe",
      iconColor:  "#2563eb",
    },
    {
      icon:       <ActivityIcon />,
      label:      t("cycleBot.insightsTab.avgDuration"),
      value:      avgDuration,
      sub:        avgDuration !== "--" ? t("cycleBot.insightsTab.regularPattern") : null,
      subColor:   "#22c55e",
      iconBg:     "#fce7f3",
      iconColor:  "#db2777",
    },
    {
      icon:       <AlertCircleIcon />,
      label:      t("cycleBot.insightsTab.alerts"),
      value:      stats?.alerts_count ?? alerts.length,
      sub:        (stats?.alerts_count ?? alerts.length) === 0 ? t("cycleBot.insightsTab.noIrregularities") : null,
      iconBg:     "#fff7ed",
      iconColor:  "#f59e0b",
    },
    {
      icon:       <CheckCircleIcon />,
      label:      t("cycleBot.insightsTab.regularity"),
      value:      regularity,
      sub:        regularityLabel,
      subColor:   "#9333ea",
      iconBg:     "#f5f3ff",
      iconColor:  "#7c3aed",
    },
  ];

  return (
    <div className="cit">
      {/* ── Stats row ── */}
      <div className="cit__stats-row">
        {STATS.map((s) => (
          <StatCard key={s.label} {...s} />
        ))}
      </div>

      {/* ── Cycle History ── */}
      <div className="cit__card">
        <div className="cit__section-header">
          <div>
            <h2 className="cit__section-title">{t("cycleBot.insightsTab.cycleHistory")}</h2>
          </div>
          <div className="cit__header-actions">
            <button className="cit__filter-btn">{t("cycleBot.insightsTab.filter")}</button>
            <button className="cit__export-btn">{t("cycleBot.insightsTab.exportData")}</button>
          </div>
        </div>

        {cycles.length > 0 ? (
          <div className="cit__history-list">
            {cycles.map((c, i) => (
              <CycleHistoryCard key={i} c={c} t={t} />
            ))}
          </div>
        ) : (
          <div className="cit__empty">
            <div className="cit__empty-icon"><CalendarIcon /></div>
            <p className="cit__empty-title">{t("cycleBot.insightsTab.trackMoreCycles")}</p>
            <p className="cit__empty-sub">
              {t("cycleBot.insightsTab.keepLogging")}
            </p>
          </div>
        )}
      </div>

      {/* ── Alerts & Notifications ── */}
      <div className="cit__card">
        <h2 className="cit__section-title" style={{ marginBottom: 16 }}>
          {t("cycleBot.insightsTab.alertsNotifications")}
        </h2>
        {alerts.length === 0 ? (
          <div className="cit__no-alerts">
            <div className="cit__no-alerts-icon"><OkCircleIcon /></div>
            <p className="cit__no-alerts-title">{t("cycleBot.insightsTab.noAlertsDetected")}</p>
            <p className="cit__no-alerts-sub">{t("cycleBot.insightsTab.cycleTrackingNormally")}</p>
          </div>
        ) : (
          <div className="cit__alert-list">
            {alerts.map((a, i) => (
              <div key={i} className={`cit__alert-item ${a.level === "critical" || a.level === "critique" ? "cit__alert-item--crit" : ""}`}>
                <span>{a.level === "critical" || a.level === "critique" ? "🚨" : "⚠️"}</span>
                <span>{a.message}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
